/**
 * sceneView.ts — Merge project/scene view limits; apply mobile_view only on mobile
 */
import { I_ConfigScene, I_Scene } from '@containers/Home/types/I_Scene';
import {
  applyMobileViewOverrides as applyMobileViewOverridesFor,
  mergeSceneView,
} from '@containers/Home/view/sceneViewConfig';
import { selectDetailProject } from '@containers/Home/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

export const sceneViewRevision = (view?: I_ConfigScene) =>
  view
    ? JSON.stringify({
        limit_view: view.limit_view,
        zoom_limit: view.zoom_limit,
        default_view: view.default_view,
        mobile_view: view.mobile_view,
        orientation: view.orientation,
      })
    : '';

export {
  DEFAULT_SCENE_VIEW,
  DEFAULT_SCENE_ZOOM_LV,
  clampZoomLv,
  shouldUseMfov,
  mergeSceneView,
} from '@containers/Home/view/sceneViewConfig';

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua))
    return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    return true;
  if (window.matchMedia('(max-width: 768px)').matches) return true;
  if (
    window.matchMedia('(hover: none) and (pointer: coarse)').matches &&
    window.matchMedia('(max-width: 1024px)').matches
  )
    return true;
  return false;
};

export const applyMobileViewOverrides = (
  baseView: I_ConfigScene,
  isMobile = isMobileDevice(),
): I_ConfigScene => applyMobileViewOverridesFor(baseView, isMobile);

export const withSceneView = (
  scene?: I_Scene,
  projectGlobalViewConfig?: I_ConfigScene,
): I_Scene | undefined => {
  if (!scene) return scene;
  const view = mergeSceneView(scene.config, projectGlobalViewConfig);
  return {
    ...scene,
    config: {
      ...scene.config,
      default_view: view.default_view,
      limit_view: view.limit_view,
      zoom_limit: view.zoom_limit,
      mobile_view: view.mobile_view,
    },
  };
};

export const useSceneView = (scene?: I_Scene) => {
  const project = useSelector(selectDetailProject);
  const globalView = project?.config?.setting?.view;
  const [isMobile, setIsMobile] = useState(() => isMobileDevice());

  useEffect(() => {
    const syncMobile = () => setIsMobile(isMobileDevice());
    syncMobile();
    const mql = window.matchMedia('(max-width: 768px)');
    mql.addEventListener?.('change', syncMobile);
    window.addEventListener('orientationchange', syncMobile);
    return () => {
      mql.removeEventListener?.('change', syncMobile);
      window.removeEventListener('orientationchange', syncMobile);
    };
  }, []);

  return useMemo(() => {
    const withView = withSceneView(scene, globalView);
    if (!withView) return withView;
    return {
      ...withView,
      config: applyMobileViewOverrides(withView.config, isMobile),
    };
  }, [scene, globalView, isMobile]);
};
